/* eslint-disable react/jsx-boolean-value */
import type { FC } from 'react';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { styled } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';
import Emoji from '@atlaskit/emoji/emoji';
import ResourcedEmoji from '@atlaskit/emoji/resourced-emoji';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import DocumentIcon from '@atlaskit/icon-file-type/glyph/document/16';
import BlogObject from '@atlaskit/object/blog';
import type { IconColor, IconColorPressed } from '@atlaskit/tokens/css-type-schema';
import SmartLinkIcon from '@atlaskit/icon/core/smart-link';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { getOptimisticImageURL, getEmojiSSRSupport } from '@confluence/fabric-emoji-support';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';

import { onFailedToLoadEmoji } from './analytics.ts';
import type { EmojiComponentProps } from './__types__/EmojiComponent.ts';
import { emojiExp, maybeOverrideFaviconWithEmoji } from './emojiMutationHelpers.ts';
import { isValidUrl } from './isValidUrl.ts';
import { getEmojiProvider, sanitizeEmojiValue } from './transformer.ts';
import { EmojiPlaceholder } from './EmojiPlaceholder.tsx';
import { useEmojiDescription } from './useEmojiDescription.ts';
import { EMOJI_SIZE_MEDIUM, EMOJI_SIZE_LARGE } from './emojiTitleConstants.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EmojiWrapper = styled.span<{
	hasCoverPicture: boolean;
	isPageTitleComponent?: boolean;
	shouldRemoveEmojiLeftPadding?: boolean;
}>({
	display: 'flex',
	justifyContent: 'center',
	overflow: 'hidden',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxWidth: ({ hasCoverPicture, isPageTitleComponent }) =>
		isPageTitleComponent
			? hasCoverPicture
				? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_LARGE}px`
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${EMOJI_SIZE_MEDIUM}px`
			: '28px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingRight: ({ hasCoverPicture, isPageTitleComponent }) =>
		isPageTitleComponent
			? hasCoverPicture
				? token('space.250')
				: token('space.100')
			: token('space.075'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	paddingLeft: ({ hasCoverPicture, isPageTitleComponent, shouldRemoveEmojiLeftPadding }) =>
		shouldRemoveEmojiLeftPadding
			? token('space.0')
			: isPageTitleComponent
				? hasCoverPicture
					? token('space.250')
					: token('space.100')
				: token('space.075'),
	paddingTop: token('space.0'),
	paddingBottom: token('space.0'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	font: ({ isPageTitleComponent }) =>
		isPageTitleComponent ? token('font.heading.xxlarge') : token('font.heading.large'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	cursor: ({ isPageTitleComponent }) => (isPageTitleComponent ? 'pointer' : 'initial'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	alignItems: ({ isPageTitleComponent }) => (isPageTitleComponent ? 'center' : 'initial'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ResourcedEmojiWrapper = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		display: 'flex',
	},
});

const EmbedImage: FC<{
	emoji: string;
	height: number;
	primaryColor?: 'currentColor' | IconColor | IconColorPressed;
}> = ({ emoji, height, primaryColor }) => {
	const intl = useIntl();
	const [hasImageError, setHasImageError] = useState(false);

	const handleImageError = useCallback(() => {
		setHasImageError(true);
	}, []);

	return hasImageError ? (
		<SmartLinkIcon
			color={primaryColor}
			label={intl.formatMessage(i18n.embed)}
			spacing="spacious"
			testId="embed-svg"
		/>
	) : (
		<img src={emoji} height={height} alt="" onError={handleImageError} />
	);
};

const EmojiComponent: FC<EmojiComponentProps> = ({
	emoji,
	primaryColor,
	context,
	height = 26,
	contentType = 'page',
	showTooltip = false,
	wrapper,
	loadingMargin,
	renderResourcedEmoji = false,
	hasCoverPicture,
	isPageTitleComponent = false,
	shouldRemoveEmojiLeftPadding = false,
}) => {
	const intl = useIntl();
	const { userId, cloudId, isLoggedIn, isLicensed } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isLivePage = useIsCurrentPageLive();
	const [{ emojiDescription, error }, { loadEmojiDescription }] = useEmojiDescription(emoji);
	const emojiId =
		typeof emoji === 'string'
			? sanitizeEmojiValue(emoji)
			: emoji && typeof emoji === 'object'
				? sanitizeEmojiValue(emoji.id)
				: null;
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = !!process.env.REACT_SSR || !!window.__SSR_RENDERED__ || renderResourcedEmoji;
	const isGuestUser = !isLicensed || !isLoggedIn;
	const isEmojiSSRSupported = useMemo(
		() =>
			getEmojiSSRSupport({
				isSSR,
				hasCloudId: !!cloudId,
				isGuestUser,
			}),
		[isSSR, cloudId, isGuestUser],
	);
	const shouldRenderResourcedEmoji = !!emojiId && !!userId && isEmojiSSRSupported;
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();

	useEffect(() => {
		if (emojiId && isPageTitleComponent) {
			const url = getOptimisticImageURL(cloudId, emojiId);
			if (url) {
				maybeOverrideFaviconWithEmoji(url);
			}
		}

		return () => {
			if (isPageTitleComponent) {
				maybeOverrideFaviconWithEmoji(null);
			}
		};
	}, [isPageTitleComponent, emojiId, cloudId]);

	// A11Y-39563: When rendering via ResourcedEmoji we still need the loaded
	// EmojiDescription so we can pass a human-readable shortName (e.g.
	// "thought_balloon") rather than the raw hex emojiId (e.g. "1f4ad") to the
	// accessible label. Behind the experiment, also load the description in the
	// ResourcedEmoji path so the shortName can be resolved.
	const shouldResolveShortNameForResourcedEmoji =
		shouldRenderResourcedEmoji &&
		expValEqualsNoExposure('platform_a11y_fixes_emoji_title_shortname', 'isEnabled', true);

	useEffect(() => {
		// EmojiComponent accept both the EmojiDescription object and an emoji id string
		// if user pass in emoji id string, has to first fetch the emoji icon using emojiProvider
		// to pass into <Emoji /> below
		if (
			(!shouldRenderResourcedEmoji || shouldResolveShortNameForResourcedEmoji) &&
			emojiId && // the emoji prop is a string, not an EmojiDescription
			!emojiDescription // emojiDescription has not been loaded yet
		) {
			loadEmojiDescription({
				emojiId,
				userId: userId!,
				cloudId,
			});
		}
	}, [
		loadEmojiDescription,
		emojiId,
		emojiDescription,
		cloudId,
		userId,
		shouldRenderResourcedEmoji,
		shouldResolveShortNameForResourcedEmoji,
	]);

	const Wrapper = wrapper || EmojiWrapper;

	/**
	 * Why do emojis need to be 'sanitized'? Originally emoji content properties
	 * (emoji-title-published, emoji-title-draft) were a part of a REST API
	 * where the value would be JSON and not just a string.  This is why emoji
	 * values come through with extra quotes and we have to clean that up.
	 * A URL with quotes is not a valid URL.
	 * e.g. '"https://www.youtube.com/s/desktop/1f2ae858/img/favicon_32x32.png"' vs.
	 * "https://www.youtube.com/s/desktop/1f2ae858/img/favicon_32x32.png"
	 */

	// If emoji is a URL, render it as an image.  Setting height on
	// the img via the prop passed in ensures it renders well across
	// various places (home cards, recents in top nav, etc.).
	if (typeof emoji === 'string') {
		const santiziedEmoji = sanitizeEmojiValue(emoji);
		if (isValidUrl(santiziedEmoji)) {
			return (
				<Wrapper
					data-testid="emoji-wrapper"
					hasCoverPicture={Boolean(hasCoverPicture)}
					isPageTitleComponent={isPageTitleComponent}
					shouldRemoveEmojiLeftPadding={shouldRemoveEmojiLeftPadding}
					data-vc="emoji-component"
					{...ssrPlaceholderIdProp}
				>
					<EmbedImage emoji={santiziedEmoji} height={height} primaryColor={primaryColor} />
				</Wrapper>
			);
		}
	}

	// render a default image if emoji is not yet set for the embed content type
	if (contentType === 'embed' && !emoji) {
		return (
			<Wrapper
				data-testid="emoji-wrapper"
				hasCoverPicture={Boolean(hasCoverPicture)}
				isPageTitleComponent={isPageTitleComponent}
				shouldRemoveEmojiLeftPadding={shouldRemoveEmojiLeftPadding}
				data-vc="emoji-component"
				{...ssrPlaceholderIdProp}
			>
				<SmartLinkIcon
					color={primaryColor}
					label={intl.formatMessage(i18n.embed)}
					testId="embed-svg"
				/>
			</Wrapper>
		);
	}

	// if emoji is unicode, render it directly
	if (typeof emoji === 'string' && emojiExp.test(emoji)) {
		return (
			<Wrapper
				data-testid="emoji-wrapper"
				hasCoverPicture={!!hasCoverPicture}
				isPageTitleComponent={isPageTitleComponent}
				shouldRemoveEmojiLeftPadding={shouldRemoveEmojiLeftPadding}
				data-vc="emoji-component"
				{...ssrPlaceholderIdProp}
			>
				{emoji}
			</Wrapper>
		);
	}

	// emoji not yet set and still loading
	if (!shouldRenderResourcedEmoji) {
		if (emojiDescription === undefined) {
			return <EmojiPlaceholder height={height} loadingMargin={loadingMargin} />;
		} else if (emojiDescription === null) {
			if (context !== 'smartCard') return null;
			// for the case of smart link/card
			// emoji not found, might be from another space or site
			// in this case return a default document/blogpost to avoid showing placeholder indefinitely
			return contentType === 'blogpost' ? (
				<BlogObject label={intl.formatMessage(i18n.blogpost)} testId="blog-icon" />
			) : (
				<DocumentIcon label={intl.formatMessage(i18n.document)} testId="document-icon" />
			);
		}
	}

	let emojiComponent = null;
	if (shouldRenderResourcedEmoji) {
		emojiComponent = (
			<ResourcedEmojiWrapper>
				<ResourcedEmoji
					emojiId={{
						id: emojiId,
						// A11Y-39563: Prefer the resolved human-readable shortName (e.g.
						// ":thought_balloon:") over the raw hex id (e.g. "1f4ad") so screen
						// readers announce a meaningful accessible name. Falls back to the
						// emojiId until the description resolves / when the experiment is off.
						shortName: shouldResolveShortNameForResourcedEmoji
							? (emojiDescription?.shortName ?? emojiId)
							: emojiId,
					}}
					emojiProvider={getEmojiProvider(cloudId, userId)}
					showTooltip={showTooltip}
					fitToHeight={height}
					optimisticImageURL={getOptimisticImageURL(cloudId, emojiId, 'XXXHDPI')}
					customFallback={<EmojiPlaceholder height={height} loadingMargin={loadingMargin} />}
					pageTitleEmoji={true}
				/>
			</ResourcedEmojiWrapper>
		);
	} else if (emojiDescription) {
		emojiComponent = (
			<Emoji
				emoji={emojiDescription}
				fitToHeight={height}
				showTooltip={showTooltip}
				// @ts-ignore
				onLoadError={() => {
					// Throw an exception here if you want to see who renders this emoji.
					// eslint-disable-next-line no-console
					console.error(`Failed to load emoji`, emoji);

					// NOTE: We are currently in the process of asking for UGC exception for shortName
					// shortName (which comes from a user entered field when uploading a custom emoji).
					// However, if this request is rejected we will have to update this analytic to exclude
					// emoji.
					onFailedToLoadEmoji({
						createAnalyticsEvent,
						emoji: emojiDescription,
						context,
						isLivePage,
					});

					// some emoji (especially custom emoji) failed to load the first time user add/publish page
					// when that happens, attempt to refetch and get emoji once
					if (emojiId && userId) {
						loadEmojiDescription({
							emojiId,
							userId,
							cloudId,
						});
					}
				}}
			/>
		);
	}

	return (
		<Wrapper
			data-testid="emoji-wrapper"
			hasCoverPicture={!!hasCoverPicture}
			isPageTitleComponent={isPageTitleComponent}
			shouldRemoveEmojiLeftPadding={shouldRemoveEmojiLeftPadding}
			data-vc="emoji-component"
			{...ssrPlaceholderIdProp}
		>
			{emojiComponent}
			{error && <ErrorDisplay error={error} />}
		</Wrapper>
	);
};

EmojiComponent.displayName = 'EmojiComponent';

export { EmojiComponent };

const i18n = defineMessages({
	blogpost: {
		id: 'emoji-title.icon.blogpost',
		defaultMessage: 'Blog',
		description:
			'The text is used as an accessible label for the icon representing the Blog content type in the page title area. It is read by screen readers to identify the content type.',
	},
	embed: {
		id: 'emoji-title.icon.embed',
		defaultMessage: 'Embed',
		description:
			'The text is used as an accessible label for the icon representing the Embed content type in the page title area. It is read by screen readers to identify the content type.',
	},
	document: {
		id: 'emoji-title.icon.document',
		defaultMessage: 'Document',
		description: "Label for the 'document' content type icon",
	},
});
